import { keyframes } from '@emotion/react';
import type { ThemeConfig } from '../../theme';

/**
 * Design tokens for the authentication screens — Stratify ERP.
 *
 * Deliberately self-contained and dependency-free.
 *
 * 1. It must not read `useAppTheme()`. The five app presets are a *tenant*
 *    white-label preference belonging to a logged-in workspace; a logged-out
 *    visitor has to see the Stratify brand regardless of whatever preset is
 *    still sitting in their localStorage from a previous session.
 * 2. It must not read `pages/landing/landingTokens`. That module is tuned for
 *    the light marketing page and evolves with it; the login screen is a dark
 *    cinematic surface with its own palette. Coupling them means every
 *    marketing repaint silently repaints the front door of the product.
 *
 * The cost is a little duplication of the brand hues, which is the cheaper
 * side of the trade.
 */
export const auth = {
  // ---- Surface ------------------------------------------------------------
  /** Base page ink. Near-black with a navy cast so glows read as light, not haze. */
  ink: '#070d20',
  inkDeep: '#04081a',
  inkMid: '#0a1330',

  /**
   * The page field: a near-black navy base with two very wide, very low-alpha
   * colour washes. Layered as one `background` shorthand so a single property
   * owns the whole field and nothing can half-override it.
   */
  pageGradient:
    'radial-gradient(1400px 760px at 12% 92%, rgba(59,110,225,0.20) 0%, rgba(59,110,225,0) 62%),' +
    'radial-gradient(1100px 620px at 88% 6%, rgba(124,92,245,0.16) 0%, rgba(124,92,245,0) 60%),' +
    'linear-gradient(165deg, #070d20 0%, #0a1330 48%, #060b1c 100%)',

  // ---- Brand --------------------------------------------------------------
  violet: '#8b5cf6',
  blue: '#4f7ff5',
  /**
   * Links and accents on the dark field. Lighter than the button blue on
   * purpose: #4f7ff5 as text on #0a1330 sits just under WCAG AA, #6f9dff clears
   * it comfortably.
   */
  accent: '#6f9dff',
  headlineGradient: 'linear-gradient(96deg, #8b5cf6 0%, #6d7ff7 52%, #4f7ff5 100%)',
  /** The primary submit fill. Three stops so the hover sweep has somewhere to go. */
  buttonGradient: 'linear-gradient(100deg, #5b5bf0 0%, #7c5cf5 50%, #4f7ff5 100%)',
  glyphGradient: 'linear-gradient(135deg, #7b52f5 0%, #3b7bf5 55%, #2aa6f0 100%)',

  // ---- Type ---------------------------------------------------------------
  text: '#f1f5ff',
  /** Body copy on the ink field. ~8:1 over the card fill. */
  textMuted: 'rgba(200,214,245,0.78)',
  /**
   * The dimmest tint any *text* may use: labels, captions, placeholders, the
   * footer. The alpha is set exactly where the composite still clears 4.5:1
   * against the card fill (the lightest surface text sits on here), so there is
   * deliberately no fainter text token to reach for.
   */
  textFaint: 'rgba(183,199,233,0.64)',

  // ---- Lines & glass ------------------------------------------------------
  hairline: 'rgba(255,255,255,0.10)',
  hairlineStrong: 'rgba(255,255,255,0.20)',
  fieldFill: 'rgba(255,255,255,0.045)',
  fieldFillFocus: 'rgba(111,157,255,0.07)',
  focusRing: '0 0 0 3px rgba(111,157,255,0.22)',

  cardRadius: 20,
  maxWidth: 1240,
} as const;

/**
 * Per-module accents for the orbit. Kept as one closed set so a module added
 * later cannot invent a ninth hue, and every hue here was checked to stay
 * distinguishable at the 38px tile size against the ink field.
 */
export const moduleAccent = {
  blue: '#4f8ef7',
  green: '#22c08a',
  slate: '#8296bd',
  amber: '#f0ab3d',
  violet: '#a78bfa',
  teal: '#19b8b8',
  orange: '#f4823a',
  indigo: '#8b5cf6',
} as const;

export type ModuleAccent = keyof typeof moduleAccent;

// ---- Keyframes ------------------------------------------------------------
// Every consumer of these must also spread `reducedMotion`; see the note there.

/** Entrance: fade and rise. Paired with `both` fill so nothing starts visible. */
export const riseIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/** The module tiles' idle drift. Small amplitude — this runs forever. */
export const floatY = keyframes`
  0%, 100% { transform: translate(-50%, -50%) translateY(0); }
  50%      { transform: translate(-50%, -50%) translateY(-9px); }
`;

/** Grid-laid variant of `floatY` (no centring transform to preserve). */
export const floatPlain = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
`;

export const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const spinReverse = keyframes`
  from { transform: rotate(360deg); }
  to   { transform: rotate(0deg); }
`;

/** Core breathing. Opacity + scale together, never scale alone — it reads flat. */
export const corePulse = keyframes`
  0%, 100% { transform: scale(1);    opacity: 0.88; }
  50%      { transform: scale(1.06); opacity: 1; }
`;

/** Connector flow. Pair with a dasharray; the offset is in user units. */
export const dashFlow = keyframes`
  from { stroke-dashoffset: 24; }
  to   { stroke-dashoffset: 0; }
`;

export const drift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
`;

// ---- Mixins ---------------------------------------------------------------

/**
 * Spread this into any `sx` that animates. It is opt-in per rule rather than a
 * single global stylesheet override because MUI's `sx` wins the cascade against
 * a plain global selector, so a global rule would silently do nothing here.
 */
export const reducedMotion = {
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none !important',
    transition: 'none !important',
  },
};

/** Text filled with the brand gradient. Headline sizes only — never body copy. */
export const gradientText = {
  background: auth.headlineGradient,
  backgroundSize: '220% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  color: 'transparent',
  animation: `${drift} 12s ease-in-out infinite`,
  ...reducedMotion,
};

/**
 * The login card. `backdropFilter` is progressive enhancement: the fill alone
 * already carries enough contrast, so browsers without it lose gloss, not
 * legibility.
 */
export const glassCard = {
  position: 'relative',
  backgroundColor: 'rgba(255,255,255,0.045)',
  border: `1px solid ${auth.hairline}`,
  borderRadius: `${auth.cardRadius}px`,
  backdropFilter: 'blur(22px) saturate(140%)',
  WebkitBackdropFilter: 'blur(22px) saturate(140%)',
  boxShadow: '0 30px 80px rgba(2,6,23,0.62), 0 2px 10px rgba(2,6,23,0.4)',
  // Inner top highlight — the single detail that makes glass read as glass
  // rather than as a flat translucent rectangle.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    padding: '1px',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0) 42%)',
    WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    pointerEvents: 'none',
  },
  // `as const` matters: without it TypeScript widens `position` to `string`,
  // which is no longer assignable to MUI's `sx` CSS object.
} as const;

/** Focus ring for controls sitting on the ink field. */
export const focusVisibleRing = {
  '&:focus-visible': {
    outline: `2px solid ${auth.accent}`,
    outlineOffset: '2px',
    borderRadius: '4px',
  },
};

/**
 * Fixed MUI theme for the auth screens, so MUI-rendered surfaces that fall
 * outside our own `sx` (the password-reset Dialog, Alerts) stay on-brand no
 * matter which preset the visitor has stored.
 */
export const authThemeConfig: ThemeConfig = {
  id: 'stratify-auth',
  name: 'Stratify Auth',
  brand: {
    name: 'Stratify ERP',
    shortName: 'Stratify',
    tagline: 'One Business. One Platform.',
    logo: '/logo192.png',
    version: '1.0.0',
  },
  colors: {
    // The dialog renders on a light surface, so primary is the deeper blue
    // that clears AA as button and link text there — not the on-dark accent.
    primary: '#3355e8',
    primaryDark: '#2540c4',
    primaryLight: '#7f9bf5',

    secondary: '#7b52f5',
    secondaryDark: '#5b34c9',
    secondaryLight: '#a78bfa',

    sidebarBg: auth.ink,
    sidebarBgGradient: auth.pageGradient,
    sidebarText: auth.text,
    sidebarTextMuted: auth.textMuted,
    sidebarActiveItem: 'rgba(255,255,255,0.15)',
    sidebarHoverItem: 'rgba(255,255,255,0.08)',
    sidebarAccent: '#7f9bf5',
    sidebarAccentGradient: 'linear-gradient(180deg, #7f9bf5 0%, #2540c4 100%)',

    headerBg: '#ffffff',
    headerBgGradient: '#ffffff',
    headerText: '#0f172a',

    background: '#f6f8fd',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#eef2fa',

    textPrimary: '#0f172a',
    textSecondary: '#4a5568',

    success: '#15803d',
    warning: '#b45309',
    error: '#b91c1c',
    info: '#3355e8',

    divider: '#e4e9f2',
    border: '#e4e9f2',
  },
};
